/**
 * Shared TypeScript types used across API and web applications.
 * These types define the data structures for users, essays, and API responses.
 */

// =============================================================================
// User Types
// =============================================================================

/**
 * User roles in the system:
 * - author: Full access, can create/edit essays and manage users
 * - editor: Can suggest edits to essays
 * - reviewer: Can comment on essays in the reviewer portal
 * - commenter: Can comment on public essays (auto-approved on signup)
 */
export type UserRole = 'author' | 'editor' | 'reviewer' | 'commenter';

/**
 * User account status:
 * - pending: Awaiting admin approval
 * - approved: Active account
 * - blocked: Account blocked from commenting
 */
export type UserStatus = 'pending' | 'approved' | 'blocked';

/**
 * User entity as stored in database and returned by API.
 * Represents a user who has authenticated via Google SSO.
 */
export interface User {
  id: number;
  googleId: string;
  email: string;
  name: string;
  avatarUrl: string | null;
  role: UserRole;
  /** Whether the user has been approved by an admin to access the system */
  approved: boolean;
  /** User account status: pending, approved, or blocked */
  status: UserStatus;
  createdAt: string;
  updatedAt: string;
}

/**
 * Public user info (safe to expose to other users).
 * Excludes sensitive fields like googleId.
 */
export interface PublicUser {
  id: number;
  name: string;
  avatarUrl: string | null;
  role: UserRole;
}

// =============================================================================
// Essay Types
// =============================================================================

/**
 * Essay workflow states:
 * - draft: Initial writing, not visible to reviewers
 * - in_review: Open for reviewer comments (Phase 3)
 * - revision: Author working on feedback (Phase 3)
 * - approved: Review complete, ready to publish (Phase 3)
 * - published: Live on public site (Phase 5)
 * - archived: Hidden from public, preserved in system (Phase 5)
 */
export type EssayStatus = 'draft' | 'in_review' | 'revision' | 'approved' | 'published' | 'archived';

/**
 * Essay entity as stored in database.
 * Represents a single essay synced from the /essays/ folder.
 */
export interface Essay {
  id: number;
  /** URL-friendly identifier derived from folder name */
  slug: string;
  /** Essay title extracted from first # heading in markdown */
  title: string;
  /** Full markdown content */
  content: string;
  /** Current workflow state */
  status: EssayStatus;
  /** ID of the author (first user / admin) */
  authorId: number;
  /** Path to source file in /essays/ folder */
  sourcePath: string;
  /** Foreign key to category (Phase 4) */
  categoryId: number | null;
  /** Whether supporting info (attachments, notes) is public (Phase 4) */
  supportingInfoPublic: boolean;
  /** SHA-256 hash of content for change detection */
  contentHash?: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Essay list item (without full content for performance).
 * Used in essay list views.
 */
export interface EssayListItem {
  id: number;
  slug: string;
  title: string;
  status: EssayStatus;
  authorId: number;
  /** Category ID (if assigned) */
  categoryId: number | null;
  /** Preview of content (first N characters) */
  preview: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Essay with author information included.
 * Used when displaying essay details with author info.
 */
export interface EssayWithAuthor extends Essay {
  author: PublicUser;
}

// =============================================================================
// Comment Types (Phase 2)
// =============================================================================

/**
 * Comment types determine how a comment is positioned:
 * - inline: Attached to specific text selection via character offsets
 * - paragraph: Attached to a paragraph by its index
 * - general: Overall essay comment with no specific position
 */
export type CommentType = 'inline' | 'paragraph' | 'general';

/**
 * Comment entity as stored in database.
 * Represents a comment on an essay with optional threading.
 * 
 * Used by:
 * - CommentSidebar component for displaying comments
 * - InlineCommentPopover for creating inline comments
 */
export interface Comment {
  id: number;
  /** ID of the essay this comment belongs to */
  essayId: number;
  /** ID of the user who wrote the comment */
  userId: number;
  /** ID of parent comment for threading (null = top-level) */
  parentId: number | null;
  /** Type of comment: inline, paragraph, or general */
  type: CommentType;
  /** For inline comments: start character offset in markdown */
  selectionStart: number | null;
  /** For inline comments: end character offset in markdown */
  selectionEnd: number | null;
  /** For paragraph comments: 0-indexed paragraph number */
  paragraphIndex: number | null;
  /** The comment text content (supports markdown) */
  content: string;
  /** Whether this comment thread is resolved */
  resolved: boolean;
  /** Whether this comment was flagged by auto-moderator */
  flagged: boolean;
  /** Reason the comment was flagged */
  flagReason: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Comment with user information included.
 * Used when displaying comments with author details.
 */
export interface CommentWithUser extends Comment {
  user: PublicUser;
}

/**
 * Comment with nested replies for threaded display.
 * Used by CommentSidebar to render threaded conversations.
 */
export interface CommentWithReplies extends CommentWithUser {
  /** Nested array of reply comments */
  replies: CommentWithReplies[];
}

// =============================================================================
// Suggestion Types (Phase 2)
// =============================================================================

/**
 * Suggestion status workflow:
 * - pending: Awaiting author review
 * - accepted: Author approved, changes applied to essay
 * - rejected: Author declined the suggestion
 */
export type SuggestionStatus = 'pending' | 'accepted' | 'rejected';

/**
 * Suggestion entity as stored in database.
 * Represents an editor's proposed text change (Google Docs style).
 * 
 * Used by:
 * - SuggestionDisplay component for showing pending suggestions
 * - InlineCommentPopover for creating suggestions
 */
export interface Suggestion {
  id: number;
  /** ID of the essay this suggestion applies to */
  essayId: number;
  /** ID of the user who made the suggestion */
  userId: number;
  /** Start character offset in markdown content */
  selectionStart: number;
  /** End character offset in markdown content */
  selectionEnd: number;
  /** The original text being replaced */
  originalText: string;
  /** The suggested replacement text */
  suggestedText: string;
  /** Current status: pending, accepted, or rejected */
  status: SuggestionStatus;
  createdAt: string;
  /** Timestamp when accepted/rejected (null if pending) */
  resolvedAt: string | null;
}

/**
 * Suggestion with user information included.
 * Used when displaying suggestions with author details.
 */
export interface SuggestionWithUser extends Suggestion {
  user: PublicUser;
}

// =============================================================================
// API Response Types
// =============================================================================

/**
 * Standard API success response wrapper.
 * All successful API responses follow this format.
 */
export interface ApiResponse<T> {
  success: true;
  data: T;
}

/**
 * Standard API error response wrapper.
 * All error responses follow this format.
 */
export interface ApiError {
  success: false;
  error: string;
}

/**
 * Union type for all API responses.
 * Enables type-safe handling of both success and error cases.
 */
export type ApiResult<T> = ApiResponse<T> | ApiError;

// =============================================================================
// Request Types
// =============================================================================

/**
 * Request body for creating a new essay via API.
 * Note: Essays are typically created via file watcher, but this allows manual creation.
 */
export interface CreateEssayRequest {
  slug: string;
  title: string;
  content: string;
  status?: EssayStatus;
}

/**
 * Request body for updating an existing essay.
 * All fields are optional - only provided fields will be updated.
 */
export interface UpdateEssayRequest {
  title?: string;
  content?: string;
  status?: EssayStatus;
  /** Content hash for conflict detection (optional) */
  expectedHash?: string;
}

// =============================================================================
// Comment Request Types (Phase 2)
// =============================================================================

/**
 * Request body for creating a new comment.
 * Type-specific fields are required based on comment type.
 * 
 * Used by:
 * - POST /api/essays/:slug/comments endpoint
 */
export interface CreateCommentRequest {
  /** Comment type determines required position fields */
  type: CommentType;
  /** The comment text content */
  content: string;
  /** Parent comment ID for replies (null = top-level) */
  parentId?: number | null;
  /** For inline comments: start character offset */
  selectionStart?: number;
  /** For inline comments: end character offset */
  selectionEnd?: number;
  /** For paragraph comments: 0-indexed paragraph number */
  paragraphIndex?: number;
}

/**
 * Request body for updating an existing comment.
 * Only the content can be updated.
 */
export interface UpdateCommentRequest {
  content: string;
}

// =============================================================================
// Suggestion Request Types (Phase 2)
// =============================================================================

/**
 * Request body for creating a new suggestion.
 * All fields are required to define the text replacement.
 * 
 * Used by:
 * - POST /api/essays/:slug/suggestions endpoint
 */
export interface CreateSuggestionRequest {
  /** Start character offset in markdown content */
  selectionStart: number;
  /** End character offset in markdown content */
  selectionEnd: number;
  /** The original text being replaced */
  originalText: string;
  /** The suggested replacement text */
  suggestedText: string;
}

// =============================================================================
// Auth Types
// =============================================================================

/**
 * Response from /auth/me endpoint.
 * Returns the currently authenticated user or null if not logged in.
 */
export interface AuthMeResponse {
  user: User | null;
}

// =============================================================================
// Workflow Types (Phase 3)
// =============================================================================

/**
 * Status transition actions for the essay workflow state machine.
 * Each action represents a valid transition from one status to another.
 * 
 * Transitions:
 * - submitForReview: draft -> in_review
 * - approve: in_review -> approved
 * - requestRevision: in_review/approved -> revision
 * - resubmit: revision -> in_review
 * - publish: draft/approved -> published
 * - unpublish: published -> draft
 * - archive: published -> archived
 * - unarchive: archived -> draft
 * 
 * Used by:
 * - StatusDropdown component for showing available actions
 * - PUT /api/essays/:slug/status endpoint for validation
 */
export type StatusAction =
  | 'submitForReview'
  | 'approve'
  | 'requestRevision'
  | 'resubmit'
  | 'publish'
  | 'unpublish'
  | 'archive'
  | 'unarchive';

/**
 * Request body for changing an essay's status.
 * 
 * Used by:
 * - PUT /api/essays/:slug/status endpoint
 * - StatusDropdown component when submitting status change
 */
export interface StatusChangeRequest {
  /** The transition action to perform */
  action: StatusAction;
  /** Optional summary describing the change (e.g., "Ready for review") */
  changeSummary?: string;
}

/**
 * Human-readable labels for status actions.
 * Used in UI for displaying action buttons/menu items.
 */
export const STATUS_ACTION_LABELS: Record<StatusAction, string> = {
  submitForReview: 'Submit for Review',
  approve: 'Approve',
  requestRevision: 'Request Revision',
  resubmit: 'Resubmit for Review',
  publish: 'Publish',
  unpublish: 'Unpublish',
  archive: 'Archive',
  unarchive: 'Unarchive',
};

/**
 * Human-readable labels for essay statuses.
 * Used in UI for displaying status badges.
 */
export const STATUS_LABELS: Record<EssayStatus, string> = {
  draft: 'Draft',
  in_review: 'In Review',
  revision: 'Revision',
  approved: 'Approved',
  published: 'Published',
  archived: 'Archived',
};

// =============================================================================
// Version History Types (Phase 3)
// =============================================================================

/**
 * Essay version entity as stored in database.
 * Represents a snapshot of an essay at a specific point in time.
 * 
 * Used by:
 * - VersionHistory component for displaying timeline
 * - DiffViewer component for comparing versions
 * - Revert functionality to restore previous content
 */
export interface EssayVersion {
  id: number;
  /** ID of the essay this version belongs to */
  essayId: number;
  /** Sequential version number (1, 2, 3, ...) */
  versionNumber: number;
  /** Full markdown content at this version */
  content: string;
  /** ID of the user who created this version */
  createdBy: number;
  /** Optional summary of what changed */
  changeSummary: string | null;
  /** Essay status at the time of this version */
  status: EssayStatus;
  /** Word count at this version */
  wordCount: number;
  /** Timestamp when version was created */
  createdAt: string;
}

/**
 * Essay version with user information included.
 * Used when displaying version history with author details.
 */
export interface EssayVersionWithUser extends Omit<EssayVersion, 'createdBy'> {
  /** User who created this version */
  createdBy: PublicUser;
}

/**
 * Diff segment representing a piece of unchanged, added, or removed text.
 * Used by diff algorithm to show changes between versions.
 */
export interface DiffSegment {
  /** Type of change: unchanged, added (new), or removed (deleted) */
  type: 'unchanged' | 'added' | 'removed';
  /** The text content for this segment */
  value: string;
}

/**
 * Result of comparing two versions.
 * Contains the diff segments and statistics about changes.
 * 
 * Used by:
 * - GET /api/essays/:slug/versions/:id/diff endpoint
 * - DiffViewer component for rendering diff visualization
 */
export interface DiffResult {
  /** Version number being compared (current) */
  currentVersion: number;
  /** Version number being compared against (previous) */
  previousVersion: number;
  /** Array of diff segments showing changes */
  diff: DiffSegment[];
  /** Statistics about the changes */
  stats: {
    /** Number of words added */
    additions: number;
    /** Number of words deleted */
    deletions: number;
    /** Net change in word count */
    wordCountChange: number;
  };
}

// =============================================================================
// Admin Types (Phase 3)
// =============================================================================

/**
 * User with activity statistics.
 * Used in admin panel for user management.
 * 
 * Used by:
 * - GET /api/admin/users endpoint
 * - UserManagement component for displaying user table
 */
export interface UserWithStats extends User {
  /** User activity statistics */
  stats: {
    /** Total number of comments made by user */
    commentsCount: number;
    /** Total number of suggestions made by user */
    suggestionsCount: number;
  };
}

/**
 * System-wide statistics for admin dashboard.
 * 
 * Used by:
 * - GET /api/admin/stats endpoint
 * - DashboardStats component for displaying metrics
 */
export interface AdminStats {
  /** Total number of registered users */
  totalUsers: number;
  /** Number of users pending approval */
  pendingUsers: number;
  /** Number of blocked users */
  blockedUsers: number;
  /** Total number of essays in system */
  totalEssays: number;
  /** Number of published essays */
  publishedEssays: number;
  /** Total number of comments */
  totalComments: number;
  /** Total number of suggestions */
  totalSuggestions: number;
  /** Number of flagged comments awaiting review */
  flaggedComments: number;
  /** Essay counts by status */
  essaysByStatus: Record<EssayStatus, number>;
}

/**
 * Request body for changing a user's role.
 * 
 * Used by:
 * - PUT /api/admin/users/:id/role endpoint
 * - UserManagement component role dropdown
 */
export interface ChangeUserRoleRequest {
  /** New role for the user */
  role: UserRole;
}

// =============================================================================
// Category Types (Phase 4)
// =============================================================================

/**
 * Category entity for organizing essays into broad topics.
 * Each essay can belong to one category (single-select).
 * 
 * Used by:
 * - GET /api/categories endpoint
 * - CategoryTagSelector component
 */
export interface Category {
  id: number;
  /** Display name for the category */
  name: string;
  /** URL-friendly identifier */
  slug: string;
  /** Optional description of the category */
  description: string | null;
  createdAt: string;
}

/**
 * Request body for creating a new category.
 * 
 * Used by:
 * - POST /api/categories endpoint
 */
export interface CreateCategoryRequest {
  name: string;
  description?: string;
}

/**
 * Request body for updating a category.
 * 
 * Used by:
 * - PUT /api/categories/:id endpoint
 */
export interface UpdateCategoryRequest {
  name?: string;
  description?: string;
}

// =============================================================================
// Tag Types (Phase 4)
// =============================================================================

/**
 * Tag entity for fine-grained essay classification.
 * Essays can have multiple tags (multi-select).
 * 
 * Used by:
 * - GET /api/tags endpoint
 * - CategoryTagSelector component
 */
export interface Tag {
  id: number;
  /** Display name for the tag */
  name: string;
  /** URL-friendly identifier */
  slug: string;
  createdAt: string;
}

/**
 * Request body for creating a new tag.
 * 
 * Used by:
 * - POST /api/tags endpoint
 */
export interface CreateTagRequest {
  name: string;
}

/**
 * Request body for setting essay tags.
 * 
 * Used by:
 * - PUT /api/essays/:slug/tags endpoint
 */
export interface SetEssayTagsRequest {
  /** Array of tag IDs to assign to the essay */
  tagIds: number[];
}

// =============================================================================
// Series Types (Phase 4)
// =============================================================================

/**
 * Series entity for grouping related essays in a specific order.
 * E.g., "Introduction to Philosophy" series with parts 1, 2, 3.
 * 
 * Used by:
 * - GET /api/series endpoint
 * - SeriesManager component
 */
export interface Series {
  id: number;
  /** Display name for the series */
  name: string;
  /** URL-friendly identifier */
  slug: string;
  /** Optional description of the series theme */
  description: string | null;
  createdAt: string;
}

/**
 * Series with its essays included and ordered.
 * 
 * Used by:
 * - GET /api/series/:slug endpoint
 * - SeriesPage component
 */
export interface SeriesWithEssays extends Series {
  /** Essays in this series, ordered by orderIndex */
  essays: Array<{
    essay: EssayListItem;
    orderIndex: number;
  }>;
}

/**
 * Information about an essay's position in a series.
 * Includes navigation to previous/next essays.
 * 
 * Used by:
 * - EssayDetailPage for series navigation
 */
export interface EssaySeriesInfo {
  /** The series this essay belongs to */
  series: Series;
  /** Position of this essay in the series (0-indexed) */
  orderIndex: number;
  /** Total number of essays in the series */
  totalInSeries: number;
  /** Previous essay in series (null if first) */
  previousEssay: EssayListItem | null;
  /** Next essay in series (null if last) */
  nextEssay: EssayListItem | null;
}

/**
 * Request body for creating a new series.
 * 
 * Used by:
 * - POST /api/series endpoint
 */
export interface CreateSeriesRequest {
  name: string;
  description?: string;
}

/**
 * Request body for updating a series.
 * 
 * Used by:
 * - PUT /api/series/:id endpoint
 */
export interface UpdateSeriesRequest {
  name?: string;
  description?: string;
}

/**
 * Request body for adding/updating an essay in a series.
 * 
 * Used by:
 * - PUT /api/essays/:slug/series endpoint
 */
export interface SetEssaySeriesRequest {
  /** ID of the series to add to */
  seriesId: number;
  /** Position in the series (0-indexed) */
  orderIndex: number;
}

// =============================================================================
// Attachment Types (Phase 4)
// =============================================================================

/**
 * Attachment entity for file attachments (images, PDFs, documents).
 * Files are stored in /opt/compendium/data/uploads/
 * 
 * Used by:
 * - GET /api/essays/:slug/attachments endpoint
 * - AttachmentsPanel component
 */
export interface Attachment {
  id: number;
  /** ID of the essay this attachment belongs to */
  essayId: number;
  /** Original filename from upload */
  filename: string;
  /** Path relative to uploads directory */
  filepath: string;
  /** MIME type (e.g., 'image/png', 'application/pdf') */
  mimeType: string;
  /** File size in bytes */
  size: number;
  /** Whether visible on public site when essay is published */
  isPublic: boolean;
  createdAt: string;
}

/**
 * Request body for toggling attachment visibility.
 * 
 * Used by:
 * - PUT /api/attachments/:id/visibility endpoint
 */
export interface SetAttachmentVisibilityRequest {
  isPublic: boolean;
}

// =============================================================================
// Notebook Types
// =============================================================================

/**
 * Notebook entity for organizing standalone author notes.
 * Author-only, never publicly accessible.
 *
 * Used by:
 * - GET /api/notebooks endpoint
 * - Notes management UI
 */
export interface Notebook {
  id: number;
  name: string;
  slug: string;
  description: string | null;
  createdAt: string;
}

/**
 * Request body for creating a new notebook.
 */
export interface CreateNotebookRequest {
  name: string;
  description?: string;
}

/**
 * Request body for updating a notebook.
 */
export interface UpdateNotebookRequest {
  name?: string;
  description?: string;
}

// =============================================================================
// Note Types
// =============================================================================

/**
 * Standalone author note organized by notebook.
 * Can be linked to essays via note_links junction table.
 * Author-only, never publicly accessible.
 *
 * Used by:
 * - GET /api/notes endpoint
 * - Notes management UI
 */
export interface Note {
  id: number;
  title: string;
  slug: string;
  content: string;
  notebookId: number;
  archivedAt: string | null;
  autoSummary?: string;
  autoTagsRaw?: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Note with linked essays included.
 */
export interface NoteWithLinks extends Note {
  linkedEssays: Array<{ id: number; slug: string; title: string }>;
}

/**
 * Request body for creating a new note.
 */
export interface CreateNoteRequest {
  title: string;
  content: string;
  notebookId: number;
}

/**
 * Request body for updating a note.
 */
export interface UpdateNoteRequest {
  title?: string;
  content?: string;
  notebookId?: number;
}

// =============================================================================
// Note Link Types
// =============================================================================

/**
 * Request body for setting all essay links on a note (replace strategy).
 */
export interface SetNoteLinksRequest {
  essayIds: number[];
}

// =============================================================================
// Search Types (Phase 4)
// =============================================================================

/**
 * Search result item with highlighted snippets.
 * 
 * Used by:
 * - GET /api/search endpoint
 * - SearchResultsPage component
 */
export interface SearchResult {
  /** The matched essay (list item format) */
  essay: EssayListItem;
  /** Title with search terms highlighted (<mark> tags) */
  titleSnippet: string;
  /** Content excerpt with search terms highlighted */
  contentSnippet: string;
  /** BM25 relevance score (lower is better) */
  rank: number;
}

/**
 * Search response with results and metadata.
 * 
 * Used by:
 * - GET /api/search endpoint
 * - SearchBar and SearchResultsPage components
 */
export interface SearchResponse {
  /** Array of search results */
  results: SearchResult[];
  /** Total number of matching results */
  total: number;
  /** The search query */
  query: string;
  /** Applied filters */
  filters: {
    category?: string;
    tag?: string;
    status?: EssayStatus;
  };
}

// =============================================================================
// Note Search Types (Phase 3 Desktop)
// =============================================================================

/**
 * Note search result with highlighted snippets.
 *
 * Used by:
 * - GET /api/notes/search endpoint
 * - NotesSidebar search component
 */
export interface NoteSearchResult {
  note: Note;
  titleSnippet: string;
  contentSnippet: string;
  rank: number;
  notebookName: string;
}

/**
 * Note search response with results and metadata.
 */
export interface NoteSearchResponse {
  results: NoteSearchResult[];
  total: number;
  query: string;
}

// =============================================================================
// Note Similarity Types (Phase 3 Metadata Janitor)
// =============================================================================

/**
 * A similar item (note or essay) found by embedding similarity.
 */
export interface SimilarItem {
  id: number;
  title: string;
  slug: string;
  similarity: number | null;
}

/**
 * Response from GET /api/notes/:id/similar.
 */
export interface SimilarItemsResponse {
  notes: SimilarItem[];
  essays: SimilarItem[];
}

// =============================================================================
// Linked Notes (Essay → Note) Types
// =============================================================================

/**
 * A standalone note as seen from the essay perspective.
 * Returned by GET /api/essays/:slug/linked-notes.
 */
export interface LinkedNoteFromEssay {
  id: number;
  title: string;
  slug: string;
  notebookName: string;
  autoSummary: string | null;
  similarity: number | null;
  source: 'manual' | 'auto';
  updatedAt: string;
}

// =============================================================================
// Table of Contents Types (Phase 4)
// =============================================================================

/**
 * Table of contents item representing a markdown heading.
 * Supports nested structure for sub-headings.
 * 
 * Used by:
 * - TableOfContents component
 */
export interface TocItem {
  /** Unique ID derived from heading text (for anchor links) */
  id: string;
  /** Heading text content */
  text: string;
  /** Heading level (1-6) */
  level: number;
  /** Nested sub-headings */
  children: TocItem[];
}

// =============================================================================
// Audio Types (Phase 5)
// =============================================================================

/**
 * A single speakable segment of an essay for TTS playback.
 * Segments are either paragraphs (including blockquotes) or headings.
 * IDs use independent counters: p-0, p-1, ... and h-0, h-1, ...
 *
 * Used by:
 * - POST /api/admin/essays/:slug/generate-audio (produces)
 * - GET /api/public/essays/:slug/audio/manifest.json (serves)
 * - TTS playback UI (Chat B) for synchronized highlighting
 */
export interface AudioSegment {
  /** Unique segment ID: "p-0", "h-1", etc. Must match DOM element IDs injected by the renderer */
  id: string;
  type: 'paragraph' | 'heading';
  /** Relative path to WAV file, e.g. "audio/p-0.wav" */
  file: string;
  /** Plain text sent to Kokoro TTS */
  text: string;
}

/**
 * Response from POST /api/admin/essays/:slug/generate-audio
 *
 * Used by:
 * - Admin Generate Audio button in EssayDetailPage
 */
export interface AudioGenerationResult {
  /** Number of segments successfully generated */
  segments: number;
  /** List of generated WAV filenames (basename only, e.g. ["p-0.wav", "h-0.wav"]) */
  files: string[];
}

// =============================================================================
// Extended Essay Types (Phase 4)
// =============================================================================

/**
 * Essay with full supporting information.
 * Includes category, tags, series membership, attachments, and notes.
 * 
 * Used by:
 * - GET /api/essays/:slug endpoint (extended response)
 * - EssayDetailPage component
 */
export interface EssayWithSupporting extends EssayWithAuthor {
  /** Category the essay belongs to (if any) */
  category: Category | null;
  /** Tags assigned to this essay */
  tags: Tag[];
  /** Series this essay belongs to (with navigation) */
  seriesInfo: EssaySeriesInfo[];
  /** File attachments */
  attachments: Attachment[];
}

/**
 * Extended essay list item with category and tag info.
 * Used for filtered essay lists.
 * 
 * Used by:
 * - EssaysPage with category/tag filters
 */
export interface EssayListItemWithMeta extends EssayListItem {
  /** Category the essay belongs to (if any) */
  category: Category | null;
  /** Tags assigned to this essay */
  tags: Tag[];
}
